import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { LeadsWebTab } from '../components/LeadsWebTab';
import {
  Upload, FileText, TrendingDown, TrendingUp, Zap, Flame, Search,
  CheckCircle, Loader2, History, User, Receipt, BarChart3,
  ChevronDown, ChevronRight, RefreshCw, ArrowRight, Euro,
  Pencil, Save, AlertTriangle, CreditCard, Camera, Download, Globe, Wifi,
  MapPin, Plus, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Comparatore = () => {
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState('analizza');
  const [file, setFile] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clients, setClients] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [quotazioni, setQuotazioni] = useState([]);
  const [loadingQuotazioni, setLoadingQuotazioni] = useState(false);
  const [expandedQuotazione, setExpandedQuotazione] = useState(null);
  const [ricalcolando, setRicalcolando] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [savingManual, setSavingManual] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [creatingPratica, setCreatingPratica] = useState(false);
  const [pendingOffer, setPendingOffer] = useState(null); // offer waiting for pagamento/bolletta dialog
  const [contractDialog, setContractDialog] = useState({ metodo_pagamento: 'rid', tipo_bolletta: 'web', iban: '' });
  const [existingClientMatch, setExistingClientMatch] = useState(null);
  const [internetOffers, setInternetOffers] = useState([]);
  const [loadingInternet, setLoadingInternet] = useState(false);
  const [internetFilter, setInternetFilter] = useState('all');
  const [allOffers, setAllOffers] = useState({ luce: [], gas: [], internet: [] });
  const [confrontoType, setConfrontoType] = useState('luce');
  const [myCompanyIds, setMyCompanyIds] = useState(new Set());
  // FiberFinder state
  const [ffCity, setFfCity] = useState('');
  const [ffCitySuggestions, setFfCitySuggestions] = useState([]);
  const [ffCityLoading, setFfCityLoading] = useState(false);
  const [ffSelectedCity, setFfSelectedCity] = useState(null);
  const [ffAddress, setFfAddress] = useState('');
  const [ffAddressSuggestions, setFfAddressSuggestions] = useState([]);
  const [ffAddressLoading, setFfAddressLoading] = useState(false);
  const [ffSelectedAddress, setFfSelectedAddress] = useState(null);
  const [ffSelectedNumber, setFfSelectedNumber] = useState('');
  const [ffResults, setFfResults] = useState(null);
  const [ffLoading, setFfLoading] = useState(false);
  const [ffUsage, setFfUsage] = useState(null);
  const [ffError, setFfError] = useState('');
  const [ffAvailableTech, setFfAvailableTech] = useState([]);
  const ffCityRef = useRef(null);
  const ffAddrRef = useRef(null);
  const ffCityTimer = useRef(null);
  const ffAddrTimer = useRef(null);

  // ==== Lead dal Web ====
  const [leadsWeb, setLeadsWeb] = useState([]);
  const [loadingLeadsWeb, setLoadingLeadsWeb] = useState(false);
  const [preparandoLead, setPreparandoLead] = useState(null); // lead_id in azione
  const [analizzandoBollettaLead, setAnalizzandoBollettaLead] = useState(null); // bolletta_id in azione

  const fetchLeadsWeb = useCallback(async () => {
    setLoadingLeadsWeb(true);
    try {
      const res = await axios.get(`${API_URL}/api/leads-web/pending`);
      setLeadsWeb(res.data || []);
    } catch (e) { toast.error('Errore nel caricamento Lead dal Web'); }
    finally { setLoadingLeadsWeb(false); }
  }, []);

  const handlePreventivoDaLead = async (leadId) => {
    setPreparandoLead(leadId);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/da-lead/${leadId}`);
      setResult(res.data);
      setTab('analizza');
      toast.success(`${res.data.num_offerte_confrontate} offerte confrontate`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impossibile creare il preventivo');
    } finally { setPreparandoLead(null); }
  };

  const handleAnalisiBollettaDaLead = async (leadId, bollettaId) => {
    setAnalizzandoBollettaLead(bollettaId);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/analizza-bolletta-portale/${leadId}/${bollettaId}`);
      setResult(res.data);
      setTab('analizza');
      toast.success(`Bolletta AI OK: ${res.data.num_offerte_confrontate} offerte`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Analisi bolletta fallita');
    } finally { setAnalizzandoBollettaLead(null); }
  };

  // Se arrivo da LeadDetail con ?quot=xxx, carico la quotazione come result
  useEffect(() => {
    const quotId = searchParams.get('quot');
    if (quotId && quotazioni.length > 0) {
      const q = quotazioni.find(x => x.id === quotId);
      if (q) {
        setResult({
          dati_bolletta: q.dati_bolletta,
          confronti: q.confronti,
          quotazione_id: q.id,
          num_offerte_confrontate: (q.confronti || []).length,
        });
        setTab('analizza');
        // Pulisco query param
        setSearchParams({});
      }
    }
  }, [quotazioni, searchParams, setSearchParams]);

  // Precarico i lead web una volta al mount
  useEffect(() => { fetchLeadsWeb(); }, [fetchLeadsWeb]);

  const fetchClients = useCallback(async () => {
    try { const res = await axios.get(`${API_URL}/api/clients`); setClients(res.data); } catch (e) {}
  }, []);

  const fetchQuotazioni = useCallback(async () => {
    setLoadingQuotazioni(true);
    try { const res = await axios.get(`${API_URL}/api/comparatore/quotazioni`); setQuotazioni(res.data); }
    catch (e) { toast.error('Errore nel caricamento dello storico'); }
    finally { setLoadingQuotazioni(false); }
  }, []);

  useEffect(() => { fetchClients(); fetchQuotazioni(); }, [fetchClients, fetchQuotazioni]);

  const fetchInternetOffers = useCallback(async () => {
    setLoadingInternet(true);
    try {
      const res = await axios.get(`${API_URL}/api/offers?tipologia=internet`);
      setInternetOffers(res.data.filter(o => o.attiva));
    } catch (e) { toast.error('Errore nel caricamento offerte internet'); }
    finally { setLoadingInternet(false); }
  }, []);

  const fetchAllOffersForConfronto = useCallback(async () => {
    try {
      const [luceRes, gasRes, inetRes, compRes] = await Promise.all([
        axios.get(`${API_URL}/api/offers?tipologia=luce`),
        axios.get(`${API_URL}/api/offers?tipologia=gas`),
        axios.get(`${API_URL}/api/offers?tipologia=internet`),
        axios.get(`${API_URL}/api/companies?attiva=true`),
      ]);
      setAllOffers({
        luce: luceRes.data.filter(o => o.attiva),
        gas: gasRes.data.filter(o => o.attiva),
        internet: inetRes.data.filter(o => o.attiva),
      });
      setMyCompanyIds(new Set(compRes.data.filter(c => c.attiva).map(c => c.id)));
    } catch (e) {}
  }, []);

  useEffect(() => { if (tab === 'internet') { fetchInternetOffers(); fetchAllOffersForConfronto(); } }, [tab, fetchInternetOffers, fetchAllOffersForConfronto]);

  // FiberFinder API via backend proxy (with Cloudflare fallback)
  // FiberFinder API via backend proxy (cloudscraper bypasses Cloudflare)

  // City autocomplete with debounce
  const ffSearchCities = (val) => {
    setFfCity(val);
    setFfSelectedCity(null);
    setFfCitySuggestions([]);
    if (ffCityTimer.current) clearTimeout(ffCityTimer.current);
    if (val.length < 2) return;
    ffCityTimer.current = setTimeout(async () => {
      setFfCityLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/autocomplete/cities`, { params: { q: val, limit: 8 } });
        setFfCitySuggestions(res.data || []);
      } catch (e) { /* ignore */ }
      finally { setFfCityLoading(false); }
    }, 300);
  };

  const ffSelectCity = (city) => {
    setFfCity(city.nome_display);
    setFfSelectedCity(city);
    setFfCitySuggestions([]);
    setFfAddress('');
    setFfSelectedAddress(null);
    setFfAddressSuggestions([]);
    setFfSelectedNumber('');
  };

  // Address autocomplete with debounce
  const ffSearchAddresses = (val) => {
    setFfAddress(val);
    setFfSelectedAddress(null);
    setFfAddressSuggestions([]);
    if (ffAddrTimer.current) clearTimeout(ffAddrTimer.current);
    const cityName = ffSelectedCity?.nome_display || ffCity;
    if (val.length < 3 || !cityName) return;
    ffAddrTimer.current = setTimeout(async () => {
      setFfAddressLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/autocomplete/addresses`, { params: { city: cityName, q: val, limit: 8 } });
        setFfAddressSuggestions(res.data || []);
      } catch (e) { /* ignore */ }
      finally { setFfAddressLoading(false); }
    }, 400);
  };

  const ffSelectAddress = (addr) => {
    setFfAddress(addr.road);
    setFfSelectedAddress(addr);
    setFfAddressSuggestions([]);
  };

  // Determine available technologies from FiberFinder results
  const extractTechnologies = (results) => {
    if (!results) return [];
    const techs = [];
    const fc = results.FC;
    if (fc) {
      if (fc.COPERTURA_BITSTREAM_NGA_VULA_FTTH === 'Attivo') techs.push('FTTH');
      if (fc.COPERTURA_BITSTREAM_NGA_VULA_FTTCab === 'Attivo') techs.push('FTTC');
      if (fc.COPERTURA_BITSTREAM_NGA_VULA_XGSPON === 'Attivo') techs.push('XGS-PON');
    }
    const of_data = results.OF;
    if (of_data && of_data.status === 'civic_connected') {
      if (!techs.includes('FTTH')) techs.push('FTTH');
    }
    if (results.FIBRE?.is_connected) {
      if (!techs.includes('FTTH')) techs.push('FTTH');
    }
    // If FWA is always available as fallback
    if (techs.length === 0) techs.push('FWA');
    return techs;
  };

  const ffDirectSearch = async () => {
    const cityName = ffSelectedCity?.nome_display || ffCity;
    if (!cityName.trim() || !ffAddress.trim() || !ffSelectedNumber) return;
    setFfLoading(true); setFfError('');
    try {
      const res = await axios.post(`${API_URL}/api/fiberfinder/results`, null, {
        params: {
          city: cityName.toUpperCase(),
          address: ffAddress.toUpperCase(),
          egon: '',
          number: ffSelectedNumber
        }
      });
      const data = res.data?.data;
      if (data) {
        setFfResults(data);
        const techs = extractTechnologies(data);
        setFfAvailableTech(techs);
      } else {
        setFfError('Nessun risultato trovato');
      }
      if (res.data?.remaining !== undefined) setFfUsage({ remaining: res.data.remaining });
    } catch (e) {
      const msg = e.response?.data?.detail || 'Errore verifica copertura';
      setFfError(msg);
    } finally { setFfLoading(false); }
  };

  const ffReset = () => {
    setFfCity(''); setFfCitySuggestions([]); setFfSelectedCity(null);
    setFfAddress(''); setFfAddressSuggestions([]); setFfSelectedAddress(null);
    setFfSelectedNumber('');
    setFfResults(null); setFfError(''); setFfAvailableTech([]);
  };

  const handleAnalyze = async () => {
    if (!file) { toast.error('Seleziona un file della bolletta'); return; }
    setAnalyzing(true); setResult(null); setEditMode(false); setEditData(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (clientId && clientId !== 'none') formData.append('client_id', clientId);
      const res = await axios.post(`${API_URL}/api/comparatore/analizza-bolletta`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
      });
      setResult(res.data);
      // Auto-enable edit mode so user can immediately review/correct
      setEditData({ ...res.data.dati_bolletta });
      toast.success('Analisi completata! Verifica i dati estratti e correggi se necessario.');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nell\'analisi della bolletta'); }
    finally { setAnalyzing(false); }
  };

  const handleConfrontaManuale = async () => {
    if (!editData) return;
    setSavingManual(true);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/confronta-manuale`, {
        dati_bolletta: editData,
        quotazione_id: result?.quotazione_id || null,
      });
      setResult(res.data);
      setEditMode(false);
      toast.success('Confronto ricalcolato con i dati corretti! Nessun credito AI consumato.');
      fetchQuotazioni();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel ricalcolo'); }
    finally { setSavingManual(false); }
  };

  const handleRicalcola = async (quotazioneId) => {
    setRicalcolando(quotazioneId);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/ricalcola/${quotazioneId}`);
      toast.success('Quotazione ricalcolata con le offerte aggiornate!');
      setResult(res.data);
      setEditData({ ...res.data.dati_bolletta });
      setTab('analizza');
      fetchQuotazioni();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore nel ricalcolo'); }
    finally { setRicalcolando(null); }
  };

  const viewQuotazione = (q) => {
    setResult({
      dati_bolletta: q.dati_bolletta,
      confronti: q.confronti,
      quotazione_id: q.id,
      bolletta_file: q.bolletta_file,
      num_offerte_confrontate: q.confronti?.length || 0,
      cliente_trovato: q.client_id ? { id: q.client_id, nome: q.client_nome } : null,
    });
    setEditData({ ...q.dati_bolletta });
    setEditMode(false);
    setTab('analizza');
  };

  const updateEditField = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
  };

  const updateEditNumeric = (field, value) => {
    const parsed = value === '' ? null : parseFloat(value);
    setEditData(prev => ({ ...prev, [field]: isNaN(parsed) ? null : parsed }));
  };

  const fmt = (v) => !v && v !== 0 ? '-' : new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);
  const fmtN = (v, d = 4) => !v && v !== 0 ? '-' : Number(v).toFixed(d);

  // Calculate spesa from whatever data source (result or editData)
  const calcSpesa = (data) => {
    if (!data) return 0;
    let spesa = 0;
    if (data.consumo_annuo_kwh && data.prezzo_energia_kwh) {
      spesa += (data.consumo_annuo_kwh * data.prezzo_energia_kwh) + ((data.quota_fissa_mensile_luce || 0) * 12);
    }
    if (data.consumo_annuo_gas_smc && data.prezzo_gas_smc) {
      spesa += (data.consumo_annuo_gas_smc * data.prezzo_gas_smc) + ((data.quota_fissa_mensile_gas || 0) * 12);
    }
    return spesa;
  };

  const spesaAttuale = calcSpesa(result?.dati_bolletta);
  const spesaEdit = editData ? calcSpesa(editData) : 0;
  const bestOffer = result?.confronti?.[0];
  const bestRisparmio = bestOffer?.risparmio_stimato_annuo;
  const bestSpesa = bestOffer?.spesa_stimata_annua;

  // Validation warnings
  const getWarnings = (data) => {
    if (!data) return [];
    const w = [];
    if (data.prezzo_energia_kwh && (data.prezzo_energia_kwh > 0.40 || data.prezzo_energia_kwh < 0.02)) {
      w.push(`Prezzo energia luce ${fmtN(data.prezzo_energia_kwh)} EUR/kWh sembra anomalo (range tipico: 0.02-0.40). Potrebbe includere tasse/oneri.`);
    }
    if (data.prezzo_gas_smc && (data.prezzo_gas_smc > 1.50 || data.prezzo_gas_smc < 0.10)) {
      w.push(`Prezzo gas ${fmtN(data.prezzo_gas_smc)} EUR/Smc sembra anomalo (range tipico: 0.10-1.50). Potrebbe includere tasse/oneri.`);
    }
    if (data.consumo_annuo_kwh && data.consumo_annuo_kwh < 100) {
      w.push(`Consumo luce ${data.consumo_annuo_kwh} kWh sembra molto basso. Verifica che sia il consumo ANNUO.`);
    }
    if (data.consumo_annuo_gas_smc && data.consumo_annuo_gas_smc < 50) {
      w.push(`Consumo gas ${data.consumo_annuo_gas_smc} Smc sembra molto basso. Verifica che sia il consumo ANNUO.`);
    }
    return w;
  };

  const warnings = getWarnings(editData || result?.dati_bolletta);

  const handleSelectOffer = async (offer) => {
    if (selectedOffer === offer.offerta_id) {
      // Second click = open confirm dialog to force user to pick metodo/bolletta
      if (!result?.quotazione_id) {
        toast.error('Salva prima la quotazione');
        return;
      }
      setPendingOffer(offer);
      setContractDialog({ metodo_pagamento: 'rid', tipo_bolletta: 'web', iban: '' });
    } else {
      setSelectedOffer(offer.offerta_id);
    }
  };

  const confirmCreatePratica = async () => {
    if (!pendingOffer || !result?.quotazione_id) return;
    if (!contractDialog.metodo_pagamento || !contractDialog.tipo_bolletta) {
      toast.error('Seleziona Metodo Pagamento e Tipo Bolletta');
      return;
    }
    if (contractDialog.metodo_pagamento === 'rid' && !contractDialog.iban.trim()) {
      toast.error('IBAN obbligatorio per RID');
      return;
    }
    setCreatingPratica(true);
    try {
      const res = await axios.post(`${API_URL}/api/comparatore/crea-pratica`, {
        quotazione_id: result.quotazione_id,
        offerta: pendingOffer,
        metodo_pagamento: contractDialog.metodo_pagamento,
        tipo_bolletta: contractDialog.tipo_bolletta,
        iban: contractDialog.metodo_pagamento === 'rid' ? contractDialog.iban : '',
      });
      if (res.data.client_was_existing) {
        toast.success(`Pratica creata e collegata al cliente esistente: ${res.data.cliente_nome}`);
      } else {
        toast.success(res.data.message || 'Pratica creata con successo!');
      }
      setSelectedOffer(null);
      setPendingOffer(null);
      window.location.href = `/contracts/${res.data.contract_id}`;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nella creazione della pratica');
    } finally {
      setCreatingPratica(false);
    }
  };

  // Auto-detect existing client by CF when a bolletta is analyzed/loaded
  useEffect(() => {
    const cf = (editData?.codice_fiscale || result?.dati_bolletta?.codice_fiscale || '').trim();
    if (!cf || cf.length < 11) {
      setExistingClientMatch(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/clients/search-cf`, { params: { cf } });
        if (cancelled) return;
        const matches = res.data || [];
        // Pick exact CF match
        const exact = matches.find(c => (c.codice_fiscale || '').toUpperCase() === cf.toUpperCase());
        setExistingClientMatch(exact || matches[0] || null);
      } catch (e) {
        if (!cancelled) setExistingClientMatch(null);
      }
    })();
    return () => { cancelled = true; };
  }, [editData?.codice_fiscale, result?.dati_bolletta?.codice_fiscale]);

  return (
    <div className="space-y-6" data-testid="comparatore-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Comparatore Offerte</h1>
        <p className="text-slate-500 mt-1">Carica una bolletta e confronta con le offerte disponibili</p>
      </div>

      <div className="flex gap-1 sm:gap-2 border-b border-slate-200 pb-0 overflow-x-auto whitespace-nowrap -mx-1 px-1">
        <button className={`shrink-0 px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition-colors ${tab === 'analizza' ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          onClick={() => setTab('analizza')} data-testid="tab-analizza">
          <BarChart3 className="w-4 h-4 inline mr-2" />Nuova Analisi
        </button>
        <button className={`shrink-0 px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition-colors ${tab === 'storico' ? 'border-sky-600 text-sky-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          onClick={() => setTab('storico')} data-testid="tab-storico">
          <History className="w-4 h-4 inline mr-2" />Storico Quotazioni ({quotazioni.length})
        </button>
        <button className={`shrink-0 px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition-colors ${tab === 'internet' ? 'border-violet-600 text-violet-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          onClick={() => setTab('internet')} data-testid="tab-internet">
          <Globe className="w-4 h-4 inline mr-2" />Offerte Internet/Fibra
        </button>
        <button className={`shrink-0 px-3 sm:px-4 py-2 font-medium text-sm border-b-2 transition-colors ${tab === 'leadsweb' ? 'border-emerald-600 text-emerald-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          onClick={() => { setTab('leadsweb'); fetchLeadsWeb(); }} data-testid="tab-leadsweb">
          <Sparkles className="w-4 h-4 inline mr-2" />Lead dal Web
          {leadsWeb.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold">
              {leadsWeb.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'analizza' && (
        <div className="space-y-6">
          <Card>
            <CardHeader><CardTitle className="flex items-center gap-2 text-lg"><Receipt className="w-5 h-5 text-sky-600" />Carica Bolletta</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Bolletta (PDF o Immagine) *</Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="cursor-pointer" data-testid="bolletta-file-input" />
                    </div>
                    <Button variant="outline" className="shrink-0 px-3" onClick={() => { const input = document.createElement('input'); input.type = 'file'; input.accept = 'image/*'; input.capture = 'environment'; input.onchange = (e) => { const f = e.target.files?.[0]; if (f) setFile(f); }; input.click(); }} data-testid="camera-btn" title="Scatta foto">
                      <Camera className="w-5 h-5" />
                      <span className="ml-1.5 hidden sm:inline">Foto</span>
                    </Button>
                  </div>
                  {file && <p className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />{file.name}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Cliente (opzionale)</Label>
                  <Select value={clientId || 'none'} onValueChange={(v) => setClientId(v === 'none' ? '' : v)}>
                    <SelectTrigger data-testid="client-select"><SelectValue placeholder="Seleziona cliente o lascia vuoto" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Rileva automaticamente</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome} {c.cognome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleAnalyze} disabled={!file || analyzing} className="w-full sm:w-auto" data-testid="analyze-btn">
                {analyzing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisi AI in corso...</> : <><Search className="w-4 h-4 mr-2" />Analizza e Confronta</>}
              </Button>
            </CardContent>
          </Card>

          {result && (
            <div className="space-y-4">
              {/* SEGMENTO CLIENTE (residenziale vs business) */}
              {(() => {
                const cf = (result?.dati_bolletta?.codice_fiscale || '').trim().toUpperCase();
                const piva = (result?.dati_bolletta?.partita_iva || '').trim();
                let seg = null;
                if (piva && /^\d{11}$/.test(piva)) seg = 'business';
                else if (cf) {
                  if (/^\d{11}$/.test(cf)) seg = 'business';
                  else if (cf.length === 16 && /[A-Z]/.test(cf)) seg = 'residenziale';
                }
                if (!seg) return null;
                return (
                  <div className={`rounded-lg px-4 py-2.5 text-sm flex items-center gap-2 border ${seg === 'business' ? 'bg-purple-50 border-purple-200 text-purple-800' : 'bg-sky-50 border-sky-200 text-sky-800'}`} data-testid="cliente-segment-badge">
                    <span className="font-semibold uppercase text-xs tracking-wider">
                      {seg === 'business' ? 'Cliente Business (P.IVA)' : 'Cliente Residenziale (CF)'}
                    </span>
                    <span className="text-xs opacity-80">- il comparatore mostra solo le offerte {seg === 'business' ? 'dedicate ad aziende' : 'per privati / famiglie'}</span>
                  </div>
                );
              })()}

              {/* BANNER RISPARMIO ANNUO */}
              {bestOffer && spesaAttuale > 0 && (
                <div className={`rounded-xl p-4 sm:p-6 ${bestRisparmio > 0 ? 'bg-emerald-50 border-2 border-emerald-300' : 'bg-amber-50 border-2 border-amber-300'}`} data-testid="risparmio-banner">
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Spesa Attuale / Anno</p>
                      <p className="text-xl sm:text-2xl font-bold text-slate-800">{fmt(spesaAttuale)}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 hidden sm:block" />
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Migliore Offerta / Anno</p>
                      <p className="text-xl sm:text-2xl font-bold text-sky-700">{fmt(bestSpesa)}</p>
                      <p className="text-xs text-slate-500">{bestOffer.offerta_nome}</p>
                    </div>
                    <div className="text-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg bg-white/80">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">
                        {bestRisparmio > 0 ? 'Risparmio' : 'Costo Agg.'}
                      </p>
                      <p className={`text-2xl sm:text-3xl font-black ${bestRisparmio > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {bestRisparmio > 0 ? '' : '+'}{fmt(Math.abs(bestRisparmio))}
                      </p>
                      <p className="text-xs text-slate-500">all'anno</p>
                    </div>
                  </div>
                </div>
              )}

              {/* TOP 3 OFFERTE - Podio consigliato AI */}
              {result.confronti && result.confronti.length > 0 && (
                <Card className="border-2 border-indigo-200 shadow-md" data-testid="top3-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Sparkles className="w-5 h-5 text-indigo-600" />
                      Top 3 Offerte Più Convenienti
                      <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]">AI</Badge>
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Ordinate per risparmio annuo stimato sulla base dei tuoi consumi</p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {result.confronti.slice(0, 3).map((c, idx) => {
                        const podium = [
                          { border: 'border-amber-300', ring: 'ring-amber-200', badgeBg: 'bg-gradient-to-br from-amber-400 to-amber-600', label: '1' },
                          { border: 'border-slate-300', ring: 'ring-slate-200', badgeBg: 'bg-gradient-to-br from-slate-400 to-slate-600', label: '2' },
                          { border: 'border-orange-300', ring: 'ring-orange-200', badgeBg: 'bg-gradient-to-br from-orange-400 to-orange-700', label: '3' },
                        ][idx];
                        const risp = c.risparmio_stimato_annuo;
                        return (
                          <div key={c.offerta_id} className={`relative rounded-xl border-2 ${podium.border} bg-white p-4 hover:shadow-lg transition-shadow ${idx === 0 ? 'ring-4 ' + podium.ring : ''}`} data-testid={`top3-offer-${idx}`}>
                            <div className={`absolute -top-3 -left-3 w-9 h-9 rounded-full ${podium.badgeBg} text-white font-black text-sm flex items-center justify-center shadow-lg ring-4 ring-white`}>
                              #{podium.label}
                            </div>
                            <div className="pt-2">
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <p className="font-bold text-slate-900 text-sm leading-tight line-clamp-2">{c.offerta_nome}</p>
                                {idx === 0 && risp > 0 && <Badge className="bg-emerald-600 text-white text-[10px] shrink-0">Top</Badge>}
                              </div>
                              <div className="flex items-center gap-2 mb-3">
                                {c.compagnia_logo ? (
                                  <img
                                    src={c.compagnia_logo}
                                    alt={c.compagnia}
                                    className="w-6 h-6 object-contain rounded bg-white border border-slate-200"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                    data-testid={`top3-logo-${idx}`}
                                  />
                                ) : (
                                  <div className="w-6 h-6 rounded bg-sky-100 text-sky-700 text-[10px] font-bold flex items-center justify-center">
                                    {(c.compagnia || '?')[0]?.toUpperCase()}
                                  </div>
                                )}
                                <p className="text-xs text-slate-500 truncate">{c.compagnia}</p>
                                {c.tipologia_cliente === 'business' && (
                                  <Badge className="text-[9px] bg-purple-100 text-purple-700 border-purple-200">BUSINESS</Badge>
                                )}
                              </div>

                              <div className="space-y-1.5 mb-3">
                                <div className="flex items-baseline justify-between text-xs">
                                  <span className="text-slate-500">Costo/anno</span>
                                  <span className="font-bold text-slate-800">{fmt(c.spesa_stimata_annua)}</span>
                                </div>
                                {risp !== null && (
                                  <div className={`flex items-baseline justify-between text-xs rounded-md px-2 py-1.5 ${risp > 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                                    <span className={`font-semibold ${risp > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                      {risp > 0 ? 'Risparmi' : 'Costo agg.'}
                                    </span>
                                    <span className={`font-black text-base ${risp > 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                                      {risp > 0 ? '-' : '+'}{fmt(Math.abs(risp))}
                                    </span>
                                  </div>
                                )}
                              </div>

                              <div className="flex gap-1 flex-wrap mb-3">
                                <Badge variant="outline" className="text-[10px]">{c.tipologia?.toUpperCase()}</Badge>
                                {c.prezzo_variabile && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Var.</Badge>}
                                {c.fonte === 'arera' && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">ARERA</Badge>}
                              </div>

                              <Button
                                size="sm"
                                className={`w-full ${selectedOffer === c.offerta_id ? 'bg-emerald-600 hover:bg-emerald-700' : idx === 0 ? 'bg-indigo-600 hover:bg-indigo-700' : ''}`}
                                variant={selectedOffer === c.offerta_id || idx === 0 ? 'default' : 'outline'}
                                disabled={creatingPratica}
                                onClick={() => handleSelectOffer(c)}
                                data-testid={`top3-select-${idx}`}
                              >
                                {selectedOffer === c.offerta_id ? (
                                  <>{creatingPratica ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}{creatingPratica ? 'Attendi...' : 'Crea Pratica'}</>
                                ) : (
                                  <><ArrowRight className="w-3.5 h-3.5 mr-1" /> Scegli</>
                                )}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* WARNINGS */}
              {warnings.length > 0 && (
                <div className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4" data-testid="warnings-box">
                  <p className="font-semibold text-amber-800 flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5" /> Attenzione - Verifica i dati</p>
                  {warnings.map((w, i) => <p key={i} className="text-sm text-amber-700 ml-7">- {w}</p>)}
                  <p className="text-sm text-amber-800 mt-2 ml-7 font-medium">
                    Clicca "Modifica Dati" per correggere i valori e ricalcolare SENZA consumare crediti AI.
                  </p>
                </div>
              )}

              {/* EXISTING CLIENT MATCH */}
              {existingClientMatch && (
                <div className="rounded-lg border-2 border-emerald-400 bg-emerald-50 p-4 flex items-start gap-3" data-testid="existing-client-banner">
                  <User className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-emerald-800">Cliente gia' presente nel CRM</p>
                    <p className="text-sm text-emerald-700 mt-0.5">
                      <span className="font-medium">{existingClientMatch.nome} {existingClientMatch.cognome}</span>
                      {existingClientMatch.codice_fiscale && <span className="text-emerald-600"> - CF: {existingClientMatch.codice_fiscale}</span>}
                    </p>
                    <p className="text-xs text-emerald-700 mt-1">
                      La pratica verra' collegata a questo profilo esistente (nessun duplicato).
                    </p>
                  </div>
                  <a href={`/clients/${existingClientMatch.id}`} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-100 flex-shrink-0" data-testid="view-existing-client-btn">
                      Vedi Cliente
                    </Button>
                  </a>
                </div>
              )}

              {/* Bolletta Data - EDITABLE */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <FileText className="w-5 h-5 text-amber-600" />Dati Estratti dalla Bolletta
                      {result.cliente_trovato && <Badge variant="default" className="ml-2 bg-emerald-600"><User className="w-3 h-3 mr-1" /> Cliente trovato</Badge>}
                    </CardTitle>
                    <div className="flex gap-2">
                      {result.bolletta_file && (
                        <a href={`${API_URL}/api/uploads/${result.bolletta_file}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="text-sky-700 border-sky-200 hover:bg-sky-50" data-testid="view-bolletta-pdf-btn">
                            <Download className="w-3.5 h-3.5 mr-1" /> Bolletta PDF
                          </Button>
                        </a>
                      )}
                      {!editMode ? (
                        <Button variant="outline" size="sm" onClick={() => { setEditMode(true); if (!editData) setEditData({ ...result.dati_bolletta }); }} data-testid="edit-bolletta-btn">
                          <Pencil className="w-3.5 h-3.5 mr-1" /> Modifica Dati
                        </Button>
                      ) : (
                        <>
                          <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditData({ ...result.dati_bolletta }); }} data-testid="cancel-edit-btn">
                            Annulla
                          </Button>
                          <Button size="sm" onClick={handleConfrontaManuale} disabled={savingManual} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-recalc-btn">
                            {savingManual ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                            Ricalcola (0 crediti AI)
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {editMode ? (
                    <EditBollettaForm data={editData} onChange={updateEditField} onChangeNumeric={updateEditNumeric} spesa={spesaEdit} fmt={fmt} fmtN={fmtN} />
                  ) : (
                    <ViewBollettaData data={result.dati_bolletta} spesa={spesaAttuale} fmt={fmt} fmtN={fmtN} />
                  )}
                </CardContent>
              </Card>

              {/* Confronti */}
              <ConfrontiTable
                result={result}
                ricalcolando={ricalcolando}
                handleRicalcola={handleRicalcola}
                fmt={fmt}
                fmtN={fmtN}
                onSelectOffer={handleSelectOffer}
                selectedOffer={selectedOffer}
                creatingPratica={creatingPratica}
              />
            </div>
          )}
        </div>
      )}

      {tab === 'storico' && (
        <StoricoQuotazioni
          quotazioni={quotazioni}
          loadingQuotazioni={loadingQuotazioni}
          expandedQuotazione={expandedQuotazione}
          setExpandedQuotazione={setExpandedQuotazione}
          viewQuotazione={viewQuotazione}
          handleRicalcola={handleRicalcola}
          ricalcolando={ricalcolando}
          fmt={fmt}
          fmtN={fmtN}
          calcSpesa={calcSpesa}
        />
      )}

      {tab === 'leadsweb' && (
        <LeadsWebTab
          leads={leadsWeb} loading={loadingLeadsWeb} onRefresh={fetchLeadsWeb}
          onPreventivo={handlePreventivoDaLead} preparandoId={preparandoLead}
          onAnalizza={handleAnalisiBollettaDaLead} analizzandoId={analizzandoBollettaLead}
          onOpen={(leadId) => navigate(`/leads/${leadId}`)}
        />
      )}

      {tab === 'internet' && (
        <div className="space-y-6">
          {/* VERIFICA COPERTURA FIBRA - FiberFinder API */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-lg"><Search className="w-5 h-5 text-violet-600" />Verifica Copertura Fibra</CardTitle>
                {ffUsage && <span className="text-xs text-slate-400">Richieste API rimaste: <span className="font-bold text-violet-600">{ffUsage.remaining}/40</span></span>}
              </div>
            </CardHeader>
            <CardContent>
              {ffError && <div className="mb-3 p-2 bg-amber-50 text-amber-700 text-sm rounded border border-amber-200">{ffError}</div>}

              {!ffResults && (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* CITY AUTOCOMPLETE */}
                    <div className="space-y-1 relative" ref={ffCityRef}>
                      <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />Comune *</Label>
                      <div className="relative">
                        <Input
                          placeholder="Inizia a digitare il comune..."
                          value={ffCity}
                          onChange={(e) => ffSearchCities(e.target.value)}
                          data-testid="ff-city-input"
                          className={ffSelectedCity ? 'border-emerald-400 bg-emerald-50/30' : ''}
                        />
                        {ffCityLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-2.5 text-violet-500" />}
                        {ffSelectedCity && <CheckCircle className="w-4 h-4 absolute right-3 top-2.5 text-emerald-500" />}
                      </div>
                      {ffCitySuggestions.length > 0 && !ffSelectedCity && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="ff-city-suggestions">
                          {ffCitySuggestions.map((c, i) => (
                            <button key={i} className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm border-b border-slate-50 last:border-0 transition-colors"
                              onClick={() => ffSelectCity(c)} data-testid={`ff-city-option-${i}`}>
                              <span className="font-medium">{c.nome_display}</span>
                              <span className="text-slate-400 ml-1">({c.sigla})</span>
                              <span className="text-xs text-slate-400 ml-2">{c.provincia}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ADDRESS AUTOCOMPLETE */}
                    <div className="space-y-1 relative" ref={ffAddrRef}>
                      <Label className="text-xs flex items-center gap-1"><MapPin className="w-3 h-3" />Via/Indirizzo *</Label>
                      <div className="relative">
                        <Input
                          placeholder={ffSelectedCity ? "Inizia a digitare la via..." : "Prima seleziona il comune"}
                          value={ffAddress}
                          onChange={(e) => ffSearchAddresses(e.target.value)}
                          disabled={!ffSelectedCity && !ffCity}
                          data-testid="ff-address-input"
                          className={ffSelectedAddress ? 'border-emerald-400 bg-emerald-50/30' : ''}
                        />
                        {ffAddressLoading && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-2.5 text-violet-500" />}
                        {ffSelectedAddress && <CheckCircle className="w-4 h-4 absolute right-3 top-2.5 text-emerald-500" />}
                      </div>
                      {ffAddressSuggestions.length > 0 && !ffSelectedAddress && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="ff-address-suggestions">
                          {ffAddressSuggestions.map((a, i) => (
                            <button key={i} className="w-full text-left px-3 py-2 hover:bg-violet-50 text-sm border-b border-slate-50 last:border-0 transition-colors"
                              onClick={() => ffSelectAddress(a)} data-testid={`ff-address-option-${i}`}>
                              <span className="font-medium">{a.road}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Numero Civico *</Label>
                      <Input placeholder="es. 120" value={ffSelectedNumber} onChange={(e) => setFfSelectedNumber(e.target.value)}
                        data-testid="ff-number-input" />
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button onClick={ffDirectSearch} disabled={ffLoading || !(ffSelectedCity || ffCity.trim()) || !ffAddress.trim() || !ffSelectedNumber}
                      className="bg-violet-600 hover:bg-violet-700" data-testid="ff-search-btn">
                      {ffLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                      Verifica Copertura
                    </Button>
                    <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50"
                      onClick={() => window.open('https://fiberfinder.it/', '_blank')} data-testid="ff-external-btn">
                      <Wifi className="w-4 h-4 mr-2" />FiberFinder.it
                    </Button>
                    <Button variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50"
                      onClick={() => window.open('https://openfiber.it/verifica-copertura/', '_blank')}>
                      <Globe className="w-4 h-4 mr-2" />Open Fiber
                    </Button>
                  </div>
                  <p className="text-[10px] text-slate-400">API FiberFinder (RapidAPI) - max 40 richieste/giorno. Usa l'autocomplete per trovare il tuo indirizzo.</p>
                </div>
              )}

              {/* Results */}
              {ffResults && (
                <div className="space-y-3">
                  <div className="p-4 bg-violet-50 rounded-lg border border-violet-200">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-violet-700">Risultati Copertura: {ffSelectedCity?.nome_display || ffCity}, {ffAddress} {ffSelectedNumber}</p>
                      {ffAvailableTech.length > 0 && (
                        <div className="flex gap-1">
                          {ffAvailableTech.map(t => (
                            <Badge key={t} className={`text-xs ${t === 'FTTH' || t === 'XGS-PON' ? 'bg-emerald-100 text-emerald-700' : t === 'FTTC' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                              {t}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="text-xs mb-2 inline-flex px-2 py-1 rounded bg-violet-100 text-violet-600">Stato: {ffResults.status || 'N/D'}</div>

                    {/* FiberCop (FC) */}
                    {ffResults.FC && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-slate-600 mb-2">FiberCop / TIM</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_FTTH && (
                            <div className="p-2 bg-white rounded border">
                              <p className="text-[10px] text-slate-400">FTTH (Fibra)</p>
                              <p className={`text-sm font-bold ${ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_FTTH === 'Attivo' ? 'text-emerald-600' : 'text-red-500'}`}>
                                {ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_FTTH}
                              </p>
                            </div>
                          )}
                          {ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_FTTCab && (
                            <div className="p-2 bg-white rounded border">
                              <p className="text-[10px] text-slate-400">FTTC (Armadio)</p>
                              <p className={`text-sm font-bold ${ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_FTTCab === 'Attivo' ? 'text-emerald-600' : 'text-red-500'}`}>
                                {ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_FTTCab}
                              </p>
                              {ffResults.FC.VELOCITA_DOWN_BITSTREAM_NGA_VULA_FTTCab && (
                                <p className="text-[10px] text-slate-500">{ffResults.FC.VELOCITA_DOWN_BITSTREAM_NGA_VULA_FTTCab}↓ / {ffResults.FC.VELOCITA_UP_BITSTREAM_NGA_VULA_FTTCab}↑ Mbps - Dist: {ffResults.FC.DISTANZA_BITSTREAM_NGA_VULA_FTTCab}m</p>
                              )}
                            </div>
                          )}
                          {ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_XGSPON && (
                            <div className="p-2 bg-white rounded border">
                              <p className="text-[10px] text-slate-400">XGS-PON (Ultra Fibra)</p>
                              <p className={`text-sm font-bold ${ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_XGSPON === 'Attivo' ? 'text-emerald-600' : 'text-red-500'}`}>
                                {ffResults.FC.COPERTURA_BITSTREAM_NGA_VULA_XGSPON}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* OpenFiber (OF) */}
                    {ffResults.OF && (
                      <div className="mt-3">
                        <p className="text-xs font-semibold text-slate-600 mb-2">Open Fiber</p>
                        <div className="p-2 bg-white rounded border">
                          <p className="text-[10px] text-slate-400">Cluster: {ffResults.OF.cluster || 'N/D'} | Stato: {ffResults.OF.status || 'N/D'}</p>
                          {ffResults.OF.of && Array.isArray(ffResults.OF.of) && ffResults.OF.of.slice(0, 5).map((item, i) => (
                            <div key={i} className="mt-1 text-xs text-slate-600">
                              <span className="font-medium">{item.PARTICELLA_TOP} {item.INDIRIZZO} {item.CIVICO}</span>
                              {item.SCALA_PALAZZINA && <span className="ml-1 text-slate-400">(Scala {item.SCALA_PALAZZINA})</span>}
                              <span className="ml-2 text-[10px]">UI: {item.STATO_UI}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* FIBRE / UNI */}
                    {ffResults.FIBRE && ffResults.FIBRE.is_connected && (
                      <div className="mt-3 p-2 bg-white rounded border">
                        <p className="text-xs font-semibold text-slate-600">Fibra diretta: <span className="text-emerald-600">Connessa</span></p>
                      </div>
                    )}

                    {/* Tecnologie disponibili + link a offerte */}
                    {ffAvailableTech.length > 0 && (
                      <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                        <p className="text-xs font-semibold text-emerald-700 mb-2">Tecnologie Disponibili a questo Indirizzo:</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          {ffAvailableTech.map(t => (
                            <span key={t} className={`px-3 py-1 rounded-full text-xs font-bold ${t === 'FTTH' || t === 'XGS-PON' ? 'bg-emerald-100 text-emerald-700' : t === 'FTTC' ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'}`}>
                              {t}
                            </span>
                          ))}
                        </div>
                        <p className="text-[10px] text-slate-500 mb-2">Le offerte internet sotto sono filtrate per le tecnologie disponibili.</p>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => {
                            window.location.href = `/contracts/new?tipologia=internet&city=${encodeURIComponent(ffSelectedCity?.nome_display || ffCity)}&address=${encodeURIComponent(ffAddress)}&number=${encodeURIComponent(ffSelectedNumber)}&tech=${encodeURIComponent(ffAvailableTech.join(','))}`;
                          }}
                          data-testid="ff-create-contract-btn">
                          <Plus className="w-4 h-4 mr-1" /> Crea Contratto Internet
                        </Button>
                      </div>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={ffReset} data-testid="ff-reset-btn">
                    <RefreshCw className="w-3 h-3 mr-1" /> Nuova ricerca
                  </Button>
                </div>
              )}

              {ffLoading && !ffResults && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-violet-500" /></div>}
            </CardContent>
          </Card>

          {/* CONFRONTO MIGLIORI OFFERTE - TUTTI GLI OPERATORI */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-lg"><BarChart3 className="w-5 h-5 text-amber-600" />Confronto Migliori Offerte</CardTitle>
                <div className="flex gap-2 items-center">
                  {ffAvailableTech.length > 0 && confrontoType === 'internet' && (
                    <span className="text-[10px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                      Filtro copertura: {ffAvailableTech.join(', ')}
                    </span>
                  )}
                  {['luce', 'gas', 'internet'].map(t => (
                    <button key={t} onClick={() => setConfrontoType(t)}
                      className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${confrontoType === t ? (t === 'luce' ? 'bg-amber-100 text-amber-700' : t === 'gas' ? 'bg-sky-100 text-sky-700' : 'bg-violet-100 text-violet-700') : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      data-testid={`confronto-${t}`}>
                      {t === 'luce' ? 'Luce' : t === 'gas' ? 'Gas' : 'Internet'}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {(() => {
                let offers = allOffers[confrontoType] || [];
                // Filter internet offers by available technology from coverage results
                if (confrontoType === 'internet' && ffAvailableTech.length > 0) {
                  offers = offers.filter(o => {
                    const tech = (o.tecnologia || '').toUpperCase();
                    // FWA is always compatible
                    if (tech === 'FWA') return true;
                    // If FTTH available, show FTTH offers
                    if (ffAvailableTech.includes('FTTH') && (tech === 'FTTH' || tech === 'XGS-PON' || tech === '')) return true;
                    // If FTTC available, show FTTC offers
                    if (ffAvailableTech.includes('FTTC') && tech === 'FTTC') return true;
                    // If XGS-PON available, show XGS-PON offers
                    if (ffAvailableTech.includes('XGS-PON') && tech === 'XGS-PON') return true;
                    // If only FWA, show only FWA
                    if (ffAvailableTech.length === 1 && ffAvailableTech[0] === 'FWA' && tech !== 'FTTH' && tech !== 'FTTC') return true;
                    // Show offers without specific tech
                    if (!tech) return true;
                    return false;
                  });
                }
                if (offers.length === 0) return <p className="text-sm text-slate-400 text-center py-6">Nessuna offerta disponibile{ffAvailableTech.length > 0 && confrontoType === 'internet' ? ' per le tecnologie disponibili al tuo indirizzo' : ''}. Sincronizza da ComparaSemplice.</p>;
                
                const sorted = [...offers].sort((a, b) => {
                  if (confrontoType === 'internet') return (a.prezzo_mensile || 999) - (b.prezzo_mensile || 999);
                  if (confrontoType === 'luce') return (a.costo_energia_kwh || 999) - (b.costo_energia_kwh || 999);
                  return (a.costo_energia_kwh_gas || 999) - (b.costo_energia_kwh_gas || 999);
                });

                return (
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-3 text-[10px] text-slate-400 mb-2 px-2">
                      <span className="w-4 h-4 rounded-full bg-emerald-100 border border-emerald-300 inline-block shrink-0"></span> Nostro operatore
                      <span className="w-4 h-4 rounded-full bg-slate-100 border border-slate-300 inline-block shrink-0"></span> Competitor
                    </div>
                    {sorted.map((offer, idx) => {
                      const isOurs = myCompanyIds.has(offer.compagnia_id);
                      let priceDisplay = '';
                      if (confrontoType === 'internet') {
                        priceDisplay = offer.prezzo_mensile ? `${offer.prezzo_mensile.toFixed(2)} EUR/mese` : (offer.prezzo_variabile || '-');
                      } else if (confrontoType === 'luce') {
                        priceDisplay = offer.costo_energia_kwh ? `${offer.costo_energia_kwh.toFixed(4)} EUR/kWh` : (offer.prezzo_variabile || '-');
                      } else {
                        priceDisplay = offer.costo_energia_kwh_gas ? `${offer.costo_energia_kwh_gas.toFixed(4)} EUR/Smc` : (offer.prezzo_variabile || '-');
                      }
                      const quotaDisplay = confrontoType === 'internet' 
                        ? (offer.tecnologia ? `${offer.tecnologia} ${offer.velocita_download || ''}Mbps` : '')
                        : (confrontoType === 'luce' 
                          ? (offer.quota_fissa_mese ? `+${offer.quota_fissa_mese.toFixed(2)}/mese` : '') 
                          : (offer.quota_fissa_mese_gas ? `+${offer.quota_fissa_mese_gas.toFixed(2)}/mese` : ''));

                      return (
                        <div key={offer.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${isOurs ? 'border-emerald-200 bg-emerald-50/40' : 'border-slate-150 bg-white'}`} data-testid={`confronto-offer-${offer.id}`}>
                          <span className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${idx === 0 ? 'bg-amber-400 text-white' : idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>{idx + 1}</span>
                          <span className={`w-2 h-2 rounded-full shrink-0 ${isOurs ? 'bg-emerald-500' : 'bg-slate-300'}`}></span>
                          <div className="flex-1 min-w-0">
                            <span className="font-medium truncate block">{offer.nome}</span>
                            <span className="text-[10px] text-slate-400">{offer.compagnia_nome || '?'} {offer.fonte ? `(${offer.fonte})` : ''}</span>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="font-bold tabular-nums">{priceDisplay}</span>
                            {quotaDisplay && <span className="block text-[10px] text-slate-400">{quotaDisplay}</span>}
                          </div>
                          {isOurs && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium shrink-0">NOSTRO</span>}
                        </div>
                      );
                    })}
                    <p className="text-[10px] text-slate-400 pt-2">{sorted.length} offerte totali | {sorted.filter(o => myCompanyIds.has(o.compagnia_id)).length} nostre | {sorted.filter(o => !myCompanyIds.has(o.compagnia_id)).length} competitor</p>
                  </div>
                );
              })()}
            </CardContent>
          </Card>

          {/* OFFERTE INTERNET */}
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle className="flex items-center gap-2 text-lg"><Wifi className="w-5 h-5 text-violet-600" />Offerte Internet / Fibra</CardTitle>
                <div className="flex gap-2">
                  {['all', 'FTTH', 'FWA'].map(f => (
                    <button key={f} onClick={() => setInternetFilter(f)}
                      className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${internetFilter === f ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                      data-testid={`internet-filter-${f}`}>
                      {f === 'all' ? 'Tutte' : f}
                    </button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingInternet ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
              ) : internetOffers.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Wifi className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>Nessuna offerta internet disponibile</p>
                  <p className="text-xs mt-1">Sincronizza da ComparaSemplice nella pagina Compagnie</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {internetOffers
                    .filter(o => internetFilter === 'all' || o.tecnologia === internetFilter)
                    .sort((a, b) => (a.prezzo_mensile || 999) - (b.prezzo_mensile || 999))
                    .map((offer, idx) => (
                    <div key={offer.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg border border-slate-200 hover:border-violet-300 transition-colors" data-testid={`internet-offer-${offer.id}`}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className="w-7 h-7 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                        <div className="min-w-0">
                          <p className="font-medium text-slate-900 truncate">{offer.nome}</p>
                          <p className="text-xs text-slate-500">{offer.compagnia_nome || offer.fonte}</p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 sm:gap-5 pl-10 sm:pl-0">
                        <Badge variant="outline" className={`text-xs ${offer.tecnologia === 'FTTH' ? 'border-violet-300 text-violet-700' : 'border-sky-300 text-sky-700'}`}>
                          {offer.tecnologia || 'FTTH'}
                        </Badge>
                        <div className="text-center">
                          <p className="text-xs text-slate-400">Velocita'</p>
                          <p className="text-sm font-mono font-medium">{offer.velocita_download || '?'}↓ / {offer.velocita_upload || '?'}↑ Mbps</p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-slate-400">Prezzo</p>
                          <p className="text-lg font-bold text-violet-700">{offer.prezzo_mensile?.toFixed(2) || '?'} <span className="text-xs font-normal">/mese</span></p>
                          {offer.prezzo_dopo_promo && offer.prezzo_dopo_promo !== offer.prezzo_mensile && (
                            <p className="text-[10px] text-slate-400">poi {offer.prezzo_dopo_promo.toFixed(2)}/mese</p>
                          )}
                        </div>
                        <div className="flex gap-2 text-[10px]">
                          {offer.modem_incluso && <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-600">Modem incl.</span>}
                          {offer.chiamate_illimitate && <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-600">Chiamate illim.</span>}
                          {offer.costo_attivazione > 0 && <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">Attiv. {offer.costo_attivazione.toFixed(0)}€</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Contract details dialog - forces user to pick metodo/bolletta before creating */}
      {pendingOffer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="contract-dialog">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <div className="flex items-start justify-between mb-1">
              <h3 className="text-lg font-semibold text-slate-900">Conferma dati pratica</h3>
              <button
                onClick={() => { setPendingOffer(null); }}
                className="text-slate-400 hover:text-slate-700 text-xl leading-none"
                data-testid="contract-dialog-close"
              >
                ×
              </button>
            </div>
            <p className="text-xs text-slate-500 mb-4">Questi dati determinano i bonus provvigione. Selezionali con attenzione.</p>

            <div className="space-y-4">
              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3">
                <p className="text-xs text-slate-500">Offerta selezionata</p>
                <p className="text-sm font-semibold text-slate-900 truncate">{pendingOffer.offerta_nome}</p>
                <p className="text-xs text-slate-500 truncate">{pendingOffer.compagnia}</p>
              </div>

              <div className="space-y-2">
                <Label>Metodo Pagamento *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['rid', 'bollettino'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setContractDialog(s => ({ ...s, metodo_pagamento: v, iban: v === 'rid' ? s.iban : '' }))}
                      className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition ${
                        contractDialog.metodo_pagamento === v
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`dialog-metodo-${v}`}
                    >
                      {v === 'rid' ? 'RID Bancario' : 'Bollettino'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tipo Bolletta *</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {['web', 'cartacea'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setContractDialog(s => ({ ...s, tipo_bolletta: v }))}
                      className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition ${
                        contractDialog.tipo_bolletta === v
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                      }`}
                      data-testid={`dialog-bolletta-${v}`}
                    >
                      {v === 'web' ? 'Bolletta Web' : 'Bolletta Cartacea'}
                    </button>
                  ))}
                </div>
              </div>

              {contractDialog.metodo_pagamento === 'rid' && (
                <div className="space-y-2">
                  <Label>IBAN *</Label>
                  <Input
                    value={contractDialog.iban}
                    onChange={(e) => setContractDialog(s => ({ ...s, iban: e.target.value.toUpperCase().replace(/\s/g, '') }))}
                    placeholder="IT60X0542811101000000123456"
                    data-testid="dialog-iban"
                  />
                </div>
              )}

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
                Bonus provvigione: <strong>RID</strong> + <strong>Bolletta Web</strong> maturano i bonus del piano provvigionale.
              </div>
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setPendingOffer(null)} disabled={creatingPratica} data-testid="dialog-cancel">
                Annulla
              </Button>
              <Button
                onClick={confirmCreatePratica}
                disabled={creatingPratica}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="dialog-confirm-create"
              >
                {creatingPratica ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                {creatingPratica ? 'Creazione...' : 'Crea Pratica'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ======== EDIT FORM ======== */
const EditBollettaForm = ({ data, onChange, onChangeNumeric, spesa, fmt, fmtN }) => {
  if (!data) return null;
  const tipo = data.tipologia || 'luce';
  const showLuce = tipo === 'luce' || tipo === 'dual';
  const showGas = tipo === 'gas' || tipo === 'dual';

  return (
    <div className="space-y-4" data-testid="edit-bolletta-form">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 flex items-center gap-2">
        <CreditCard className="w-4 h-4 text-sky-600" />
        <p className="text-sm text-sky-800">Modifica i valori qui sotto e clicca <strong>"Ricalcola"</strong> per aggiornare il confronto <strong>senza consumare crediti AI</strong>.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-1">
          <Label className="text-xs">Cliente</Label>
          <Input value={data.nome_cliente || ''} onChange={e => onChange('nome_cliente', e.target.value)} data-testid="edit-nome-cliente" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Fornitore Attuale</Label>
          <Input value={data.fornitore_attuale || ''} onChange={e => onChange('fornitore_attuale', e.target.value)} data-testid="edit-fornitore" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tipologia</Label>
          <Select value={data.tipologia || 'luce'} onValueChange={v => onChange('tipologia', v)}>
            <SelectTrigger data-testid="edit-tipologia"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="luce">Luce</SelectItem>
              <SelectItem value="gas">Gas</SelectItem>
              <SelectItem value="dual">Dual (Luce + Gas)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Tipo Cliente</Label>
          <Select value={data.tipo_cliente || ''} onValueChange={v => onChange('tipo_cliente', v)}>
            <SelectTrigger data-testid="edit-tipo-cliente"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="domestico_residente">Domestico Residente</SelectItem>
              <SelectItem value="domestico_non_residente">Domestico Non Residente</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="condominio">Condominio</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
        <p className="text-xs font-semibold text-slate-600 mb-2">Indirizzo Fornitura</p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 space-y-1">
            <Label className="text-[10px]">Via / Indirizzo</Label>
            <Input value={data.indirizzo_fornitura || data.indirizzo || ''} onChange={e => onChange('indirizzo_fornitura', e.target.value)} data-testid="edit-indirizzo" placeholder="Via Roma 1" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px]">Città / Paese</Label>
            <Input value={data.citta || ''} onChange={e => onChange('citta', e.target.value)} data-testid="edit-citta" placeholder="Milano" />
          </div>
          <div className="flex gap-2">
            <div className="space-y-1 w-1/2">
              <Label className="text-[10px]">Prov.</Label>
              <Input value={data.provincia || ''} onChange={e => onChange('provincia', e.target.value)} maxLength={2} data-testid="edit-provincia" placeholder="MI" />
            </div>
            <div className="space-y-1 w-1/2">
              <Label className="text-[10px]">CAP</Label>
              <Input value={data.cap || ''} onChange={e => onChange('cap', e.target.value)} maxLength={5} data-testid="edit-cap" placeholder="20100" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="space-y-1 flex-1">
          <Label className="text-xs">Spesa Energia Pura / Anno</Label>
          <p className="text-lg font-bold text-sky-700 pt-1">{fmt(spesa)}</p>
        </div>
      </div>

      {showLuce && (
        <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
          <p className="text-sm font-semibold text-amber-700 flex items-center gap-1 mb-3"><Zap className="w-4 h-4" /> LUCE - Dati Energia</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Consumo Annuo (kWh) *</Label>
              <Input type="number" step="1" value={data.consumo_annuo_kwh ?? ''} onChange={e => onChangeNumeric('consumo_annuo_kwh', e.target.value)}
                className="font-mono" data-testid="edit-consumo-luce" placeholder="es. 2700" />
              <p className="text-[10px] text-slate-400">Se la bolletta copre meno di 12 mesi, inserisci il consumo annuo stimato</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Prezzo Energia (EUR/kWh) *</Label>
              <Input type="number" step="0.0001" value={data.prezzo_energia_kwh ?? ''} onChange={e => onChangeNumeric('prezzo_energia_kwh', e.target.value)}
                className={`font-mono ${data.prezzo_energia_kwh && (data.prezzo_energia_kwh > 0.40 || data.prezzo_energia_kwh < 0.02) ? 'border-red-400 bg-red-50' : ''}`}
                data-testid="edit-prezzo-luce" placeholder="es. 0.085" />
              <p className="text-[10px] text-slate-400">Solo materia prima energia. Range tipico: 0.02-0.40 EUR/kWh</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quota Fissa Mensile (EUR)</Label>
              <Input type="number" step="0.01" value={data.quota_fissa_mensile_luce ?? ''} onChange={e => onChangeNumeric('quota_fissa_mensile_luce', e.target.value)}
                className="font-mono" data-testid="edit-quota-luce" placeholder="es. 9.90" />
            </div>
          </div>
        </div>
      )}

      {showGas && (
        <div className="bg-sky-50 rounded-lg p-4 border border-sky-200">
          <p className="text-sm font-semibold text-sky-700 flex items-center gap-1 mb-3"><Flame className="w-4 h-4" /> GAS - Dati Energia</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Consumo Annuo (Smc) *</Label>
              <Input type="number" step="1" value={data.consumo_annuo_gas_smc ?? ''} onChange={e => onChangeNumeric('consumo_annuo_gas_smc', e.target.value)}
                className="font-mono" data-testid="edit-consumo-gas" placeholder="es. 1200" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Prezzo Gas (EUR/Smc) *</Label>
              <Input type="number" step="0.0001" value={data.prezzo_gas_smc ?? ''} onChange={e => onChangeNumeric('prezzo_gas_smc', e.target.value)}
                className={`font-mono ${data.prezzo_gas_smc && (data.prezzo_gas_smc > 1.50 || data.prezzo_gas_smc < 0.10) ? 'border-red-400 bg-red-50' : ''}`}
                data-testid="edit-prezzo-gas" placeholder="es. 0.45" />
              <p className="text-[10px] text-slate-400">Solo materia prima gas. Range tipico: 0.10-1.50 EUR/Smc</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Quota Fissa Mensile (EUR)</Label>
              <Input type="number" step="0.01" value={data.quota_fissa_mensile_gas ?? ''} onChange={e => onChangeNumeric('quota_fissa_mensile_gas', e.target.value)}
                className="font-mono" data-testid="edit-quota-gas" placeholder="es. 7.50" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-1">
          <Label className="text-xs">POD</Label>
          <Input value={data.pod || ''} onChange={e => onChange('pod', e.target.value)} data-testid="edit-pod" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">PDR</Label>
          <Input value={data.pdr || ''} onChange={e => onChange('pdr', e.target.value)} data-testid="edit-pdr" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Potenza Impegnata (kW)</Label>
          <Input type="number" step="0.1" value={data.potenza_impegnata ?? ''} onChange={e => onChangeNumeric('potenza_impegnata', e.target.value)} data-testid="edit-potenza" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Scadenza Condizioni</Label>
          <Input type="date" value={data.scadenza_condizioni_economiche || ''} onChange={e => onChange('scadenza_condizioni_economiche', e.target.value)} data-testid="edit-scadenza" />
        </div>
      </div>
    </div>
  );
};

/* ======== VIEW BOLLETTA DATA (read-only) ======== */
const ViewBollettaData = ({ data, spesa, fmt, fmtN }) => (
  <div>
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      <InfoItem label="Cliente" value={data.nome_cliente} />
      <InfoItem label="Fornitore Attuale" value={data.fornitore_attuale} />
      <InfoItem label="Tipo" value={data.tipologia?.toUpperCase()} icon={data.tipologia === 'gas' ? Flame : Zap} />
      <InfoItem label="Spesa Energia Pura / Anno" value={fmt(spesa)} highlight />
    </div>
    {(data.indirizzo_fornitura || data.indirizzo || data.citta) && (
      <div className="mt-3 p-2 bg-slate-50 rounded-lg border border-slate-200">
        <p className="text-[10px] text-slate-400 mb-1">Indirizzo Fornitura</p>
        <p className="text-sm text-slate-700">
          {data.indirizzo_fornitura || data.indirizzo}
          {data.citta && `, ${data.citta}`}
          {data.provincia && ` (${data.provincia})`}
          {data.cap && ` - ${data.cap}`}
        </p>
        {data.tipo_cliente && (
          <span className="text-[10px] text-slate-400 mt-0.5 block capitalize">{data.tipo_cliente.replace('_', ' ')}</span>
        )}
      </div>
    )}
    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      {(data.tipologia === 'luce' || data.tipologia === 'dual') && (
        <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
          <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-2"><Zap className="w-3.5 h-3.5" /> LUCE</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <InfoItem label="Consumo annuo" value={data.consumo_annuo_kwh ? `${data.consumo_annuo_kwh.toLocaleString('it-IT')} kWh` : '-'} />
            <InfoItem label="Prezzo energia" value={data.prezzo_energia_kwh ? `${fmtN(data.prezzo_energia_kwh)} €/kWh` : '-'} />
            <InfoItem label="Quota fissa" value={data.quota_fissa_mensile_luce ? `${fmtN(data.quota_fissa_mensile_luce, 2)} €/mese` : '-'} />
          </div>
        </div>
      )}
      {(data.tipologia === 'gas' || data.tipologia === 'dual') && (
        <div className="bg-sky-50 rounded-lg p-3 border border-sky-200">
          <p className="text-xs font-semibold text-sky-700 flex items-center gap-1 mb-2"><Flame className="w-3.5 h-3.5" /> GAS</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            <InfoItem label="Consumo annuo" value={data.consumo_annuo_gas_smc ? `${data.consumo_annuo_gas_smc.toLocaleString('it-IT')} Smc` : '-'} />
            <InfoItem label="Prezzo gas" value={data.prezzo_gas_smc ? `${fmtN(data.prezzo_gas_smc)} €/Smc` : '-'} />
            <InfoItem label="Quota fissa" value={data.quota_fissa_mensile_gas ? `${fmtN(data.quota_fissa_mensile_gas, 2)} €/mese` : '-'} />
          </div>
        </div>
      )}
    </div>
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
      <InfoItem label="POD" value={data.pod} />
      <InfoItem label="PDR" value={data.pdr} />
      <InfoItem label="Potenza" value={data.potenza_impegnata ? `${data.potenza_impegnata} kW` : null} />
      <InfoItem label="Scadenza Condizioni" value={data.scadenza_condizioni_economiche} />
    </div>
  </div>
);

/* ======== CONFRONTI TABLE ======== */
const ConfrontiTable = ({ result, ricalcolando, handleRicalcola, fmt, fmtN, onSelectOffer, selectedOffer, creatingPratica }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex flex-col sm:flex-row sm:items-center gap-2 text-lg">
        <span className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-sky-600" />
          Confronto Offerte ({result.num_offerte_confrontate})
        </span>
        {result.quotazione_id && (
          <Button variant="outline" size="sm" className="sm:ml-auto w-full sm:w-auto" onClick={() => handleRicalcola(result.quotazione_id)}
            disabled={ricalcolando === result.quotazione_id} data-testid="ricalcola-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${ricalcolando === result.quotazione_id ? 'animate-spin' : ''}`} />
            Ricalcola
          </Button>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {result.confronti.length === 0 ? (
        <p className="text-slate-500 text-center py-8">Nessuna offerta attiva disponibile.</p>
      ) : (
        <div className="space-y-3">
          {result.confronti.map((c, idx) => {
            const hasLuce = c.spesa_stimata_luce !== null;
            const hasGas = c.spesa_stimata_gas !== null;
            return (
            <div key={c.offerta_id} className={`rounded-lg border ${idx === 0 && c.risparmio_stimato_annuo > 0 ? 'border-emerald-300 bg-emerald-50' : 'border-slate-200 bg-white'}`} data-testid={`confronto-row-${idx}`}>
              {/* Mobile layout */}
              <div className="p-4 sm:hidden">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      <p className="font-semibold text-slate-900 text-sm truncate">{c.offerta_nome}</p>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{c.compagnia}</p>
                    <div className="flex gap-1 mt-1">
                      <Badge variant="outline" className="text-[10px]">{c.tipologia?.toUpperCase()}</Badge>
                      {c.prezzo_variabile && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">Var.</Badge>}
                      {c.fonte === 'arera' && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">ARERA</Badge>}
                      {c.fonte === 'segugio' && <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-50">Segugio</Badge>}
                      {idx === 0 && c.risparmio_stimato_annuo > 0 && <Badge className="bg-emerald-600 text-[10px]">Top</Badge>}
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-xs text-slate-500">Anno</p>
                    <p className="font-bold text-slate-800">{fmt(c.spesa_stimata_annua)}</p>
                    {c.risparmio_stimato_annuo !== null && (
                      <p className={`text-sm font-bold ${c.risparmio_stimato_annuo > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        {c.risparmio_stimato_annuo > 0 ? '-' : '+'}{fmt(Math.abs(c.risparmio_stimato_annuo))}
                      </p>
                    )}
                  </div>
                </div>
                {(hasLuce || hasGas) && (
                  <div className="flex gap-2 mt-2 pt-2 border-t border-slate-100">
                    {hasLuce && <div className="flex-1 text-xs"><Zap className="w-3 h-3 text-amber-500 inline mr-1" />{fmtN(c.costo_energia_kwh)} EUR/kWh</div>}
                    {hasGas && <div className="flex-1 text-xs"><Flame className="w-3 h-3 text-sky-500 inline mr-1" />{fmtN(c.costo_energia_smc_gas)} EUR/Smc</div>}
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-slate-100">
                  {selectedOffer === c.offerta_id ? (
                    <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" onClick={() => onSelectOffer(c)} disabled={creatingPratica} data-testid={`crea-pratica-mobile-${c.offerta_id}`}>
                      {creatingPratica ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                      {creatingPratica ? 'Creazione...' : 'Crea Pratica'}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="w-full" onClick={() => onSelectOffer(c)} data-testid={`select-offer-mobile-${c.offerta_id}`}>
                      <ArrowRight className="w-4 h-4 mr-1" /> Seleziona Offerta
                    </Button>
                  )}
                </div>
              </div>
              {/* Desktop layout */}
              <div className="hidden sm:block">
                <div className="flex items-center justify-between px-5 py-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-500 text-sm font-bold">{idx + 1}</div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-900">{c.offerta_nome}</p>
                        <Badge variant="outline" className="text-xs">{c.tipologia === 'dual' ? 'DUAL' : c.tipologia?.toUpperCase()}</Badge>
                        {c.prezzo_variabile && <Badge variant="outline" className="text-xs border-amber-300 text-amber-700 bg-amber-50">Variabile</Badge>}
                        {c.fonte === 'arera' && <Badge variant="outline" className="text-xs border-emerald-300 text-emerald-700 bg-emerald-50">ARERA</Badge>}
                        {c.fonte === 'segugio' && <Badge variant="outline" className="text-xs border-blue-300 text-blue-700 bg-blue-50">Segugio</Badge>}
                        {idx === 0 && c.risparmio_stimato_annuo > 0 && <Badge className="bg-emerald-600 text-xs">Migliore</Badge>}
                        {c.risparmio_stimato_annuo > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-xs font-bold flex items-center gap-1" data-testid={`risparmio-badge-${idx}`}>
                            <TrendingDown className="w-3 h-3" />
                            Risparmi {fmt(c.risparmio_stimato_annuo)}/anno
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500">{c.compagnia}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-right">
                    <div>
                      <p className="text-xs text-slate-500">Costo Annuo</p>
                      <p className="font-bold text-lg text-slate-800">{fmt(c.spesa_stimata_annua)}</p>
                    </div>
                    <div className="min-w-[140px] px-4 py-2 rounded-lg bg-white/80 border">
                      <p className="text-xs text-slate-500">{c.risparmio_stimato_annuo > 0 ? 'Risparmio' : 'Diff.'}</p>
                      {c.risparmio_stimato_annuo !== null ? (
                        <p className={`font-black text-xl flex items-center justify-end gap-1 ${c.risparmio_stimato_annuo > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                          {c.risparmio_stimato_annuo > 0 ? <TrendingDown className="w-5 h-5" /> : <TrendingUp className="w-5 h-5" />}
                          {fmt(Math.abs(c.risparmio_stimato_annuo))}
                        </p>
                      ) : <p className="text-slate-400 text-lg">N/D</p>}
                    </div>
                  </div>
                </div>
                {(hasLuce || hasGas) && (
                  <div className="border-t px-5 py-3 flex gap-4">
                    {hasLuce && (
                      <div className="flex-1 bg-amber-50/50 rounded px-3 py-2">
                        <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-1"><Zap className="w-3 h-3" /> Luce</p>
                        <div className="flex gap-4 text-sm">
                          <span className="text-slate-600">{c.costo_energia_kwh ? `${fmtN(c.costo_energia_kwh)} EUR/kWh` : '-'}</span>
                          <span className="text-slate-600">Quota: {fmt(c.quota_fissa_mese)}/mese</span>
                          <span className="font-medium">Anno: {fmt(c.spesa_stimata_luce)}</span>
                        </div>
                      </div>
                    )}
                    {hasGas && (
                      <div className="flex-1 bg-sky-50/50 rounded px-3 py-2">
                        <p className="text-xs font-semibold text-sky-700 flex items-center gap-1 mb-1"><Flame className="w-3 h-3" /> Gas</p>
                        <div className="flex gap-4 text-sm">
                          <span className="text-slate-600">{c.costo_energia_smc_gas ? `${fmtN(c.costo_energia_smc_gas)} EUR/Smc` : '-'}</span>
                          <span className="text-slate-600">Quota: {fmt(c.quota_fissa_mese_gas)}/mese</span>
                          <span className="font-medium">Anno: {fmt(c.spesa_stimata_gas)}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center shrink-0">
                      {selectedOffer === c.offerta_id ? (
                        <Button
                          size="sm"
                          onClick={() => onSelectOffer(c)}
                          disabled={creatingPratica}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`crea-pratica-${c.offerta_id}`}
                        >
                          {creatingPratica ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                          {creatingPratica ? 'Creazione...' : 'Crea Pratica'}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onSelectOffer(c)}
                          data-testid={`select-offer-${c.offerta_id}`}
                        >
                          <ArrowRight className="w-4 h-4 mr-1" /> Seleziona
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>);
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

/* ======== STORICO QUOTAZIONI ======== */
const StoricoQuotazioni = ({ quotazioni, loadingQuotazioni, expandedQuotazione, setExpandedQuotazione, viewQuotazione, handleRicalcola, ricalcolando, fmt, fmtN, calcSpesa }) => (
  <Card>
    <CardContent className="pt-6">
      {loadingQuotazioni ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : quotazioni.length === 0 ? (
        <div className="text-center py-12 text-slate-500">
          <History className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p>Nessuna quotazione salvata</p>
        </div>
      ) : (
        <div className="space-y-3">
          {quotazioni.map((q) => {
            const isExpanded = expandedQuotazione === q.id;
            const qSpesa = calcSpesa(q.dati_bolletta);
            const qBest = q.confronti?.[0];
            return (
            <div key={q.id} className="border rounded-lg overflow-hidden" data-testid={`quotazione-${q.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-5 py-4 cursor-pointer hover:bg-slate-50 transition-colors gap-2"
                onClick={() => setExpandedQuotazione(isExpanded ? null : q.id)}>
                <div className="flex items-center gap-3 min-w-0">
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />}
                  <Receipt className="w-5 h-5 text-sky-600 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{q.client_nome || q.dati_bolletta?.nome_cliente || 'Cliente sconosciuto'}</p>
                    <div className="flex flex-wrap items-center gap-1 text-sm text-slate-500">
                      <span className="truncate">{q.dati_bolletta?.fornitore_attuale || '-'}</span>
                      {q.dati_bolletta?.tipologia && <Badge variant="outline" className="text-xs">{q.dati_bolletta.tipologia.toUpperCase()}</Badge>}
                      {q.fonte === 'form_esterno' && <Badge className="bg-violet-100 text-violet-700 text-xs">Form Esterno</Badge>}
                      {q.dati_corretti_manualmente && <Badge className="bg-blue-100 text-blue-700 text-xs">Dati corretti</Badge>}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4 sm:gap-6 text-right pl-8 sm:pl-0 shrink-0">
                  <div>
                    <p className="text-xs text-slate-500">Spesa/Anno</p>
                    <p className="text-sm font-bold">{qSpesa > 0 ? fmt(qSpesa) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">Risparmio</p>
                    <p className={`text-sm font-bold ${qBest?.risparmio_stimato_annuo > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {qBest?.risparmio_stimato_annuo > 0 ? fmt(qBest.risparmio_stimato_annuo) : '-'}
                    </p>
                  </div>
                  <p className="text-xs text-slate-400 hidden sm:block">{new Date(q.created_at).toLocaleDateString('it-IT')}</p>
                </div>
              </div>
              {isExpanded && (
                <div className="border-t bg-slate-50/50 px-4 sm:px-5 py-4 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    <InfoItem label="Consumo Luce" value={q.dati_bolletta?.consumo_annuo_kwh ? `${q.dati_bolletta.consumo_annuo_kwh.toLocaleString('it-IT')} kWh` : '-'} />
                    <InfoItem label="Consumo Gas" value={q.dati_bolletta?.consumo_annuo_gas_smc ? `${q.dati_bolletta.consumo_annuo_gas_smc.toLocaleString('it-IT')} Smc` : '-'} />
                    <InfoItem label="Prezzo Energia" value={q.dati_bolletta?.prezzo_energia_kwh ? `${fmtN(q.dati_bolletta.prezzo_energia_kwh)} EUR/kWh` : '-'} />
                    <InfoItem label="Prezzo Gas" value={q.dati_bolletta?.prezzo_gas_smc ? `${fmtN(q.dati_bolletta.prezzo_gas_smc)} EUR/Smc` : '-'} />
                    <InfoItem label="POD" value={q.dati_bolletta?.pod} />
                    <InfoItem label="PDR" value={q.dati_bolletta?.pdr} />
                  </div>
                  {q.confronti && q.confronti.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-semibold text-slate-700">Confronto Offerte</h4>
                      {q.confronti.map((c, idx) => (
                        <div key={idx} className={`flex flex-col sm:flex-row sm:items-center justify-between px-3 sm:px-4 py-2 rounded border text-sm gap-1 ${idx === 0 && c.risparmio_stimato_annuo > 0 ? 'border-emerald-200 bg-emerald-50' : 'bg-white border-slate-200'}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-5 h-5 rounded-full bg-slate-100 text-xs flex items-center justify-center font-bold text-slate-500 shrink-0">{idx + 1}</span>
                            <span className="font-medium truncate">{c.offerta_nome}</span>
                            <span className="text-slate-400 hidden sm:inline">({c.compagnia})</span>
                          </div>
                          <div className="flex items-center gap-3 sm:gap-6 pl-7 sm:pl-0">
                            <span className="text-xs sm:text-sm">Anno: <strong>{fmt(c.spesa_stimata_annua)}</strong></span>
                            {c.risparmio_stimato_annuo !== null && (
                              <span className={`text-xs sm:text-sm font-bold ${c.risparmio_stimato_annuo > 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                                {c.risparmio_stimato_annuo > 0 ? '-' : '+'}{fmt(Math.abs(c.risparmio_stimato_annuo))}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button size="sm" variant="outline" onClick={() => viewQuotazione(q)} data-testid={`view-quotazione-${q.id}`}>
                      <FileText className="w-3.5 h-3.5 mr-1" /> Vedi / Modifica
                    </Button>
                    <Button size="sm" onClick={() => handleRicalcola(q.id)} disabled={ricalcolando === q.id} data-testid={`ricalcola-quotazione-${q.id}`}>
                      <RefreshCw className={`w-3.5 h-3.5 mr-1 ${ricalcolando === q.id ? 'animate-spin' : ''}`} />
                      {ricalcolando === q.id ? 'Ricalcolo...' : 'Ricalcola'}
                    </Button>
                    {q.bolletta_file && (
                      <a href={`${API_URL}/api/uploads/${q.bolletta_file}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="outline" className="text-sky-700 border-sky-200 hover:bg-sky-50" data-testid={`download-bolletta-${q.id}`}>
                          <Download className="w-3.5 h-3.5 mr-1" /> Bolletta Originale
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>);
          })}
        </div>
      )}
    </CardContent>
  </Card>
);

const InfoItem = ({ label, value, icon: Icon, highlight }) => (
  <div className="space-y-1">
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`text-sm font-medium flex items-center gap-1 ${highlight ? 'text-sky-700' : 'text-slate-900'}`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {value || '-'}
    </p>
  </div>
);

export default Comparatore;
